/* ============================================================
   Navbar — sticky, semi-transparent with blur (iOS style).
   Shows session state: displayName + role + logout, or "Войти".
   UI-1 (edbdcb80, Artem 2026-08-26 «футер не подходит для всех
   вкладок, их может становиться больше и больше, и уже сейчас
   едет верстка из-за этого»): the row used to wrap (flex-wrap)
   with every tab inline, so growing tabs stacked the sticky
   header into multiple rows and broke the 56px layout.
   Re-architected as an adaptive single row that NEVER wraps:
     - desktop ≥768px: data-driven inline tabs (navigation.ts) in
       a horizontally scrollable .nav-inline strip — N tabs never
       break the layout, extras scroll;
     - mobile <768px: inline strip + wordmark + desktop session
       block hidden; a hamburger (.nav-menu-button) opens the
       NavDrawer portal (full tab list + session hub).
   ============================================================ */

import { useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { ROLE_LABELS } from '../types/auth';
import type { Page } from '../App';
import { NotificationBell } from './NotificationBell';
import { NavDrawer } from './NavDrawer';
import { navItems } from './navigation';
import type { NotificationTarget } from './notificationsView';

interface Props {
  page: Page;
  onNavigate: (page: Page) => void;
  /** CB-1: opens the profile drawer (authenticated users). */
  onOpenProfile?: () => void;
  /** s9 (d2de0c1c): notification row deep-link nav (kind→surface map). */
  onNotificationNavigate?: (target: NotificationTarget) => void;
}

function NavLink({
  page, current, onNavigate, label,
}: { page: Page; current: Page; onNavigate: (p: Page) => void; label: string }) {
  return (
    <button
      onClick={() => onNavigate(page)}
      style={{
        fontSize: 'var(--fs-body)',
        fontWeight: current === page ? 'var(--fw-semibold)' : 'var(--fw-medium)',
        color: current === page ? 'var(--color-primary)' : 'var(--color-text-secondary)',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );
}

export function Navbar({ page, onNavigate, onOpenProfile, onNotificationNavigate }: Props) {
  const { auth, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const session = { authenticated: auth !== null, role: auth?.role ?? null };
  const items = navItems(session);

  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        height: 'var(--navbar-height)',
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: '1px solid var(--color-separator)',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <div
        className="container navbar-inner"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          gap: 'var(--space-md)',
        }}
      >
        {/* Logo — wordmark collapses to the icon on mobile (CSS class) */}
        <button
          onClick={() => onNavigate('quote')}
          style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}
        >
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none" style={{ flexShrink: 0 }}>
            <rect x="4" y="4" width="20" height="20" rx="6" fill="var(--color-primary)" />
            <path
              d="M14 9L19 12V18L14 21L9 18V12L14 9Z"
              stroke="#FFFFFF"
              strokeWidth="1.8"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
          <span
            className="navbar-logo-text"
            style={{
              fontSize: 'var(--fs-h3)',
              fontWeight: 'var(--fw-bold)',
              color: 'var(--color-text-primary)',
              whiteSpace: 'nowrap',
            }}
          >
            PrintMarketplace
          </span>
        </button>

        {/* Navigation — desktop: scrollable inline strip (never wraps);
            mobile: hidden, replaced by the NavDrawer hamburger (CSS classes) */}
        <nav className="nav-inline" aria-label="Основная навигация" style={{ flex: 1, minWidth: 0 }}>
          {items.map((item) => (
            <NavLink key={item.page} page={item.page} current={page} onNavigate={onNavigate} label={item.label} />
          ))}
        </nav>

        {/* Session area — the profile/logout (or Войти) block is hidden on
            mobile-width screens; its mobile home is the NavDrawer session
            section. The bell stays outside the block so notifications stay
            reachable on mobile (UI-2 regression guard). */}
        {auth && <NotificationBell onNavigate={onNotificationNavigate} />}
        <div className="navbar-session">
          {auth ? (
            <>
              <button
                onClick={onOpenProfile}
                disabled={!onOpenProfile}
                title="Профиль"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  lineHeight: 1.2,
                  background: 'none',
                  border: 'none',
                  cursor: onOpenProfile ? 'pointer' : 'default',
                  padding: 0,
                }}
              >
                <span
                  style={{
                    fontSize: 'var(--fs-caption)',
                    fontWeight: 'var(--fw-semibold)',
                    color: 'var(--color-text-primary)',
                    maxWidth: 160,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={auth.displayName}
                >
                  {auth.displayName}
                </span>
                <span
                  className="badge badge-warning"
                  style={{ fontSize: 'var(--fs-overline)', padding: '1px 8px', marginTop: 2 }}
                >
                  {ROLE_LABELS[auth.role] ?? auth.role}
                </span>
              </button>
              <button
                onClick={() => void logout()}
                style={{
                  fontSize: 'var(--fs-body)',
                  fontWeight: 'var(--fw-medium)',
                  color: 'var(--color-text-secondary)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                Выйти
              </button>
            </>
          ) : (
            <button
              onClick={() => onNavigate('auth')}
              className="btn btn-primary"
              style={{
                height: 36,
                padding: '6px 16px',
                fontSize: 'var(--fs-body)',
                borderRadius: 'var(--radius-lg)',
                whiteSpace: 'nowrap',
              }}
            >
              Войти
            </button>
          )}
        </div>

        {/* Mobile hamburger (<768px only) — opens the NavDrawer portal */}
        <button
          className="nav-menu-button"
          aria-label="Меню"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(true)}
          style={{
            alignItems: 'center',
            justifyContent: 'center',
            width: 44,
            height: 44,
            color: 'var(--color-text-primary)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden>
            <path d="M3 5h16M3 11h16M3 17h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <NavDrawer
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        page={page}
        session={session}
        onNavigate={onNavigate}
        onOpenProfile={onOpenProfile}
        onLogout={() => void logout()}
      />
    </header>
  );
}
